import React, { useCallback, useEffect, useRef, useState } from 'react';

import Grid from '@mui/material/Grid2';

import {
  Character,
  CharacterFeature,
  Proficiency,
  CharacterProficiency,
  Tool,
  CharacterSpell,
  CharacterItem,
  SummonOption2,
  PseudoItem,
  SKILLS,
  ARMOR_TYPES,
  WEAPON_KEYWORDS,
  Weapon,
} from "@dndModels";
import { ModelBase } from "@models";

import DataUtilities from '@dndUtilities/data_utilities';

// import CharacterProficiencyInput from "./CharacterProficiencyInput";
// import DisplayObjects from '../../display/DisplayObjects';
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import CharacterListOptionsInput from "@dndComponents/character/misc/CharacterListOptionsInput";
import DisplayObjects from "@dndComponents/display/DisplayObjects";
import { DataType } from '@dndModels/Types';
// import DisplayObjects from '../../display/DisplayObjects';
// import SelectStringBox from '../../../../../components/input/selects/SelectStringBox';

interface Props {
  character: Character;
  char_feature: CharacterFeature;
}

const data_util = DataUtilities.getInstance();

export default function CharacterProficienciesInput({ 
  character,
  char_feature,
}: Props) {
  const selected_options = char_feature.feature_options as CharacterProficiency[];
  const [reloading, setReloading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [prof_ids, setProfIds] = useState<string[]>(selected_options.map(o => o.proficiency_id).filter(o => o !== ""));
  const [select_changed, setSelectChanged] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const recalculated = useCallback(() => {
    setReloading(true);
  }, [setReloading]);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      character.subscription_holder.subscribe("Recalculated", mounted, recalculated);
    }
  }, [character, fetched, setFetched, mounted, recalculated]);
  
  useEffect(() => {
    if (select_changed) {
      // debugger;
      setSelectChanged(false);
      const options = char_feature.feature_options as CharacterProficiency[];
      const remove_us = options.map(o => o.proficiency_id).filter(o => o !== "" && !prof_ids.includes(o));
      for (const prof_id of remove_us) {
        const opt = options.find(o => o.proficiency_id === prof_id); // options.find(o => o.proficiency_id === "");
        if (opt) {
          opt.proficiency_id = "";
        }
      }
      for (const prof_id of prof_ids) {
        let opt = options.find(o => o.proficiency_id === prof_id); // options.find(o => o.proficiency_id === "");
        if (!opt) {
          opt = options.find(o => o.proficiency_id === "");
          if (opt) {
            opt.proficiency_id = prof_id;
          }
        }
      }
      character.changed();
      character.subscription_holder.recalculate();
    }
  }, [character, char_feature, prof_ids, select_changed]);
  
  const ff = char_feature.floating_feature as Proficiency;
  let name = ff.name;
  if (ff.proficiency_types.length === 1) {
    name = ff.proficiency_types[0];
  }
  
  const renderOptionsForProficiencyType = (prof_type: string) => {
    if (prof_type === "Skill" || prof_type === "Tool") {
      let use_proficiencies: any[] = prof_type === "Skill" ? SKILLS : data_util.tools;
      // debugger;
      if (ff.the_proficiencies.length > 0 && ff.the_proficiencies.filter(o => o.proficiency_id === "Any").length === 0) {
        if (prof_type === "Skill") {
          use_proficiencies = SKILLS.filter(o => 
            ff.the_proficiencies.filter(o2 => o2.proficiency_id === o._id).length > 0
          );
        } else {
          use_proficiencies = data_util.tools.filter(o => 
            ff.the_proficiencies.filter(o2 => o2.proficiency_id === o._id).length > 0
          );
        }
      }
      if (prof_type === "Tool" && ff.tool_types.length > 0) {
        use_proficiencies = use_proficiencies.filter(o => o instanceof Tool && ff.tool_types.includes(o.tool_type));
      }
      return (
        <Grid size={12} key={prof_type}>
          <CharacterListOptionsInput
            data_type={prof_type.toLocaleLowerCase() as DataType}
            character={character}
            choose_max={char_feature.feature_options.length} 
            use_objects={use_proficiencies}
            selected={prof_ids} 
            force_selectable
            quick_fields={["ability_score"]}
            onChange={(changed: string[]) => {
              // console.log(changed);
              setProfIds(changed);
              setSelectChanged(true);
            }} 
            getDisabledReasons={(obj: ModelBase | CharacterSpell | CharacterItem | SummonOption2 | PseudoItem) => {
              const options = char_feature.feature_options as CharacterProficiency[];
              if (options.filter(o => o.proficiency_id === obj._id).length === 0) {
                if ((obj as any).data_type && (obj as any).data_type === "skill") {
                  if (character.skill_proficiencies.filter(o => o.proficiency_id === obj._id && o.prof_feature_id !== char_feature.true_id).length > 0) {
                    return "Already Known";
                  }
                } else if (obj instanceof Tool) {
                  if (character.tool_proficiencies.filter(o => o.proficiency_id === obj._id && o.prof_feature_id !== char_feature.true_id).length > 0) {
                    return "Already Known";
                  }
                }
              }
              return "";
            }}
          />
        </Grid>
      );
    } else if (prof_type === "Armor") {
      let use_proficiencies = ARMOR_TYPES;
      if (ff.the_proficiencies.length > 0 && ff.the_proficiencies.filter(o => o.proficiency_id === "Any").length === 0) {
        use_proficiencies = ARMOR_TYPES.filter(o => 
          ff.the_proficiencies.filter(o2 => o2.proficiency_id === o._id).length > 0
        );
      }
      return (
        <Grid
          key={prof_type}
          size={12}>
          <CharacterListOptionsInput
            data_type={prof_type}
            character={character}
            choose_max={char_feature.feature_options.length} 
            use_objects={use_proficiencies}
            selected={prof_ids} 
            force_selectable
            onChange={(changed: string[]) => {
              // console.log(changed);
              setProfIds(changed);
              setSelectChanged(true);
            }} 
            getDisabledReasons={(obj: ModelBase | CharacterSpell | CharacterItem | SummonOption2 | PseudoItem) => {
              const options = char_feature.feature_options as CharacterProficiency[];
              if (options.filter(o => o.proficiency_id === obj._id).length === 0) {
                if ((obj as any).data_type && (obj as any).data_type === "skill") {
                  if (character.skill_proficiencies.filter(o => o.proficiency_id === obj._id && o.prof_feature_id !== char_feature.true_id).length > 0) {
                    return "Already Known";
                  }
                } else if (obj instanceof Tool) {
                  if (character.tool_proficiencies.filter(o => o.proficiency_id === obj._id && o.prof_feature_id !== char_feature.true_id).length > 0) {
                    return "Already Known";
                  }
                }
              }
              return "";
            }}
          />
        </Grid>
      );
    } else if (prof_type === "Weapon") {
      let use_proficiencies = WEAPON_KEYWORDS;
      if (ff.the_proficiencies.length > 0 && ff.the_proficiencies.filter(o => o.proficiency_id === "Any").length === 0) {
        use_proficiencies = WEAPON_KEYWORDS.filter(o => 
          ff.the_proficiencies.filter(o2 => o2.proficiency_id === o._id).length > 0
        );
      }
      return (
        <Grid
          key={prof_type}
          size={12}>
          <CharacterListOptionsInput
            data_type={prof_type}
            character={character}
            choose_max={char_feature.feature_options.length} 
            use_objects={use_proficiencies}
            selected={prof_ids} 
            force_selectable
            onChange={(changed: string[]) => {
              // console.log(changed);
              setProfIds(changed);
              setSelectChanged(true);
            }} 
            getDisabledReasons={(obj: ModelBase | CharacterSpell | CharacterItem | SummonOption2 | PseudoItem) => {
              const options = char_feature.feature_options as CharacterProficiency[];
              if (options.filter(o => o.proficiency_id === obj._id).length === 0) {
                if ((obj as any).data_type && (obj as any).data_type === "skill") {
                  if (character.skill_proficiencies.filter(o => o.proficiency_id === obj._id && o.prof_feature_id !== char_feature.true_id).length > 0) {
                    return "Already Known";
                  }
                } else if (obj instanceof Tool) {
                  if (character.tool_proficiencies.filter(o => o.proficiency_id === obj._id && o.prof_feature_id !== char_feature.true_id).length > 0) {
                    return "Already Known";
                  }
                }
              }
              return "";
            }}
          />
        </Grid>
      );
    } else if (prof_type === "Special") {
      let use_proficiencies = data_util.items.filter(o => o instanceof Weapon);
      if (ff.the_proficiencies.length > 0 && ff.the_proficiencies.filter(o => o.proficiency_id === "Any").length === 0) {
        use_proficiencies = use_proficiencies.filter(o => 
          ff.the_proficiencies.filter(o2 => o2.proficiency_id === o._id).length > 0
        );
      }
      return (
        <Grid
          key={prof_type}
          size={12}>
          <CharacterListOptionsInput
            data_type="special"
            character={character}
            choose_max={char_feature.feature_options.length} 
            use_objects={use_proficiencies}
            selected={prof_ids} 
            force_selectable
            onChange={(changed: string[]) => {
              // console.log(changed);
              setProfIds(changed);
              setSelectChanged(true);
            }} 
            getDisabledReasons={(obj: ModelBase | CharacterSpell | CharacterItem | SummonOption2 | PseudoItem) => {
              const options = char_feature.feature_options as CharacterProficiency[];
              if (options.filter(o => o.proficiency_id === obj._id).length === 0) {
                if ((obj as any).data_type && (obj as any).data_type === "skill") {
                  if (character.skill_proficiencies.filter(o => o.proficiency_id === obj._id && o.prof_feature_id !== char_feature.true_id).length > 0) {
                    return "Already Known";
                  }
                } else if (obj instanceof Tool) {
                  if (character.tool_proficiencies.filter(o => o.proficiency_id === obj._id && o.prof_feature_id !== char_feature.true_id).length > 0) {
                    return "Already Known";
                  }
                }
              }
              return "";
            }}
          />
        </Grid>
      );
    }
  }

  // debugger;
  if (ff.proficiency_types.length > 1 || 
    ff.proficiency_types[0] === "Skill" || 
    ff.proficiency_types[0] === "Tool") {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          className="header centered"
          size={12}>
          { name }
        </Grid>
        <Grid size={12}>
          <DisplayStringWithReplacements display_string={ ff.description } cfs={character} />
        </Grid>
        { ff.proficiency_types.map(prof_type => renderOptionsForProficiencyType(prof_type)) }
      </Grid>
    );
  } else {
    if (ff.choice_count === ff.the_proficiencies.length) {
      return (
        <Grid container spacing={0} direction="row">
          <Grid size={12}>
            { ff.proficiency_types[0] === "Saving Throw" ? 
              <>
                <b>Saving Throw Proficiencies:</b> { ff.the_proficiencies.map(prof => prof.proficiency_id).join(", ") }
              </>
            : ff.proficiency_types[0] === "Special" ?
              <>
                <b>Special Weapon Proficiencies:</b> <DisplayObjects type="item" ids={ff.the_proficiencies.map(prof => prof.proficiency_id)} />
              </>
            : ff.proficiency_types[0] === "Armor" ?
              <>
                <b>Armor Training:</b> <DisplayObjects type="armor_type" ids={ff.the_proficiencies.map(prof => prof.proficiency_id)} />
              </>
            : 
              <>
                <b>Weapon Proficiencies:</b> <DisplayObjects type="weapon_keyword" ids={ff.the_proficiencies.map(prof => prof.proficiency_id)} />
              </>
            }
          </Grid>
        </Grid>
      );
    } else {
      console.log(ff);
      const prof_type = ff.proficiency_types[0];
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            className="header centered"
            size={12}>
            { name }
          </Grid>
          <Grid size={12}>
            <DisplayStringWithReplacements display_string={ ff.description } cfs={character} />
          </Grid>
          { renderOptionsForProficiencyType(prof_type) }
        </Grid>
      );
    }
    // return <></>
  }
}
